import React, { useState, useContext, useEffect } from 'react';
import { Routes, Route, NavLink } from 'react-router-dom';
import { AuthContext } from './AuthContext';
import axios from 'axios';
import Home from './Home';
import Return from './Return';
import Profile from './Profile';
import History from './History';
import QrCode from './QrCode';
import tbsLogo from './images/logos/beer-logo.svg';
import beerLoader from './images/animations/beerLoader.gif';
import hamburgerIcon from './images/assets/Menu.svg';

const subNavLinks = [
  { to: '/profile', label: 'Profile' },
  { to: '/history', label: 'Transaction History' },
  { to: '/qr-code', label: 'QR Code' },
];

function AppWrapper() {
  const { tbsUser, setTbsUser, setCustomerId } = useContext(AuthContext);
  const [authStatus, setAuthStatus] = useState('checking');
  const [authError, setAuthError]   = useState('');
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isAtTop, setIsAtTop]       = useState(true);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const code   = params.get('code');

    if (code) {
      params.delete('code');
      const cleanSearch = params.toString();
      window.history.replaceState(
        null, '',
        window.location.pathname + (cleanSearch ? `?${cleanSearch}` : '')
      );
      axios.post('/api/auth/login', { code })
        .then(res => {
          setTbsUser(res.data.user);
          setCustomerId(res.data.customerId);
          setAuthStatus('authenticated');
        })
        .catch(err => {
          setAuthError(err.response?.status === 401
            ? 'The access link has expired. Please return to The Beer Store website and try again.'
            : 'An error occurred during sign-in. Please try again.');
          setAuthStatus('error');
        });
    } else {
      axios.get('/api/auth/me')
        .then(res => {
          setTbsUser(res.data);
          setCustomerId(res.data.customerId);
          setAuthStatus('authenticated');
        })
        .catch(err => {
          if (err.response?.status === 401) {
            setAuthStatus('unauthenticated');
          } else {
            setAuthError('An error occurred. Please try again.');
            setAuthStatus('error');
          }
        });
    }
  }, []);

  useEffect(() => {
    const onScroll = () => setIsAtTop(window.scrollY === 0);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const handleLogout = async () => {
    try {
      await axios.post('/api/auth/logout');
      setTbsUser(null);
      setCustomerId(null);
      setAuthStatus('unauthenticated');
    } catch (err) {
      console.error('Logout failed:', err);
    }
  };

  if (authStatus === 'checking') {
    return (
      <div className="overlay">
        <div className="animationContainer">
          <img src={beerLoader} alt="loading" className="loadingAnimation" />
          <p>Signing in...</p>
        </div>
      </div>
    );
  }

  if (authStatus === 'unauthenticated' || authStatus === 'error') {
    return (
      <div className="page-content">
        <div className="login-form-container">
          <div className="content-center">
            <img src={tbsLogo} className="tbsLogo-no-access" alt="TBS Logo" />
            <h2>Access Required</h2>
            <p>{authError || 'Please access this application from The Beer Store website.'}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div>
      <nav className="navBar">
        {!isMenuOpen && (
          <button className="hamburger" onClick={() => setIsMenuOpen(true)} aria-label="Open menu">
            <img src={hamburgerIcon} alt="Menu" />
          </button>
        )}

        <NavLink to="/" className="lsNav">
          <img src={tbsLogo} className="tbsLogo" alt="TBS Logo" />
        </NavLink>

        <div className="rsNav desktop-nav">
          {tbsUser && (
            <span className="userBar">{tbsUser.first_name} {tbsUser.last_name}</span>
          )}
          <button onClick={handleLogout} className="btn-logout">Log Out</button>
        </div>

        {isMenuOpen && (
          <div className="mobile-menu" onClick={() => setIsMenuOpen(false)}>
            <button className="close-btn" onClick={() => setIsMenuOpen(false)}>×</button>
            <div className="mobile-menu-content" onClick={e => e.stopPropagation()}>
              {tbsUser && (
                <div className="mobile-user">
                  <span>{tbsUser.first_name} {tbsUser.last_name}</span>
                </div>
              )}
              {subNavLinks.map(({ to, label }) => (
                <NavLink key={to} to={to} className="mobile-item"
                  onClick={() => setIsMenuOpen(false)}>
                  {label}
                </NavLink>
              ))}
              <div className="mobile-item"
                onClick={() => { handleLogout(); setIsMenuOpen(false); }}>
                Log Out
              </div>
            </div>
          </div>
        )}
      </nav>

      <div className={`sub-nav${isAtTop ? '' : ' sub-nav--hidden'}`}>
        {subNavLinks.map(({ to, label }) => (
          <NavLink key={to} to={to}
            className={({ isActive }) => `sub-nav-link${isActive ? ' active' : ''}`}>
            {label}
          </NavLink>
        ))}
      </div>

      <Routes>
        <Route path="/"                element={<Home />}    />
        <Route path="/return"          element={<Return />}  />
        <Route path="/return/:labelId" element={<Return />}  />
        <Route path="/history"         element={<History />} />
        <Route path="/profile"         element={<Profile />} />
        <Route path="/qr-code"         element={<QrCode />} />
      </Routes>
    </div>
  );
}

export default AppWrapper;
